// src/components/Common/CategorySelector.js
import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Box
} from '@mui/material';
import { supabase } from '../../services/supabaseClient';

const CategorySelector = ({ value, onChange, rtlTextFieldProps }) => {
  const [categories, setCategories] = useState([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // When value changes externally, update our local state
  useEffect(() => {
    if (value && categories.length > 0) {
      // Find which main category contains this subcategory
      for (const mainCat of categories) {
        const subCat = mainCat.subcategories.find(sub => sub.id === value);
        if (subCat) {
          setSelectedMainCategory(mainCat.id);
          setSelectedSubCategory(value);
          break;
        }
      }
    }
  }, [value, categories]);

  const fetchCategories = async () => {
    try {
      // Fetch main categories
      const { data: mainCategories, error: mainError } = await supabase
        .from('categories')
        .select('*')
        .is('parent_id', null)
        .order('name', { ascending: true });

      if (mainError) throw mainError;

      // Fetch subcategories
      const { data: subCategories, error: subError } = await supabase
        .from('categories')
        .select('*')
        .not('parent_id', 'is', null)
        .order('name', { ascending: true });

      if (subError) throw subError;

      // Organize categories and subcategories
      const categoriesWithSubs = mainCategories.map(cat => ({
        ...cat,
        subcategories: subCategories.filter(sub => sub.parent_id === cat.id)
      }));

      setCategories(categoriesWithSubs);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      
    }
  };

  // Get subcategories for selected main category
  const getAvailableSubcategories = () => {
    if (!selectedMainCategory) return [];
    const mainCat = categories.find(cat => cat.id === selectedMainCategory);
    return mainCat ? mainCat.subcategories : [];
  };

  // Handle main category change
  const handleMainCategoryChange = (e) => {
    const mainCatId = e.target.value;
    setSelectedMainCategory(mainCatId);
    setSelectedSubCategory(null);
    onChange(null); // Clear the selected subcategory when main category changes
  };

  // Handle subcategory change
  const handleSubCategoryChange = (e) => {
    const subCatId = e.target.value;
    setSelectedSubCategory(subCatId);
    onChange(subCatId); // Update the form with the selected subcategory
  };

  // Get category name for display
  const getCategoryNameById = (categoryId) => {
    if (!categoryId) return '';

    // Check subcategories in all main categories
    for (const mainCat of categories) {
      const subCategory = mainCat.subcategories.find(sub => sub.id === categoryId);
      if (subCategory) {
        return `${mainCat.name} - ${subCategory.name}`;
      }
    }

    return '';
  };

  return (
    <Grid container spacing={2}>
      {/* Subcategory Grid Item */}
      <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
        <FormControl 
          fullWidth 
          disabled={!selectedMainCategory || getAvailableSubcategories().length === 0}
        >
          <InputLabel 
            id="sub-category-select-label"
            {...rtlTextFieldProps.InputLabelProps}
            sx={{
              fontWeight: 'bold',
              left: 'auto',                // anchor on the right
              // only apply our offset when NOT shrunk:
              '&:not(.MuiInputLabel-shrink)': {
                right: 45,   
                // theme.spacing(2) → 16px
              },
              '&.MuiInputLabel-shrink': {
                right: 1,   // e.g. theme.spacing(1) → 8px
                // default MUI shrink transform is translate(X, Y) scale(0.75).
                // For RTL, X is negative, so we pick a small negative shift:
                transform: 'translateX(-0.5px) translateY(-6px) scale(0.75)',
              },
            }}
          >
            الفئة الفرعية
          </InputLabel>
          <Select
            labelId="sub-category-select-label"
            value={selectedSubCategory || ''}
            onChange={handleSubCategoryChange}
            label="الفئة الفرعية"
            inputProps={rtlTextFieldProps.inputProps}
            sx={{...rtlTextFieldProps.sx, '& .MuiSelect-select': { fontWeight: 'bold', textAlign: 'right'}}}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300
                }
              }
            }}
          >
            <MenuItem value="" disabled sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
              <em>اختر الفئة الفرعية</em>
            </MenuItem>
            {getAvailableSubcategories().map((subCat) => (
              <MenuItem 
                key={subCat.id} 
                value={subCat.id}
                sx={{ fontWeight: 'bold', flexDirection: 'row-reverse' }}
              >
                {subCat.name}
              </MenuItem>
            ))}
          </Select>
          {selectedSubCategory && (
            <FormHelperText sx={{ textAlign: 'right' }}>
              {getCategoryNameById(selectedSubCategory)}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Main Category Grid Item */}
      <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
        <FormControl fullWidth>
          <InputLabel 
            id="main-category-select-label"
            {...rtlTextFieldProps.InputLabelProps}
            sx={{
              fontWeight: 'bold',
              left: 'auto',                // anchor on the right
              // only apply our offset when NOT shrunk:
              '&:not(.MuiInputLabel-shrink)': {
                right: 45,   
                // theme.spacing(2) → 16px
              },
              '&.MuiInputLabel-shrink': {
                right: 0.25,   // e.g. theme.spacing(1) → 8px
                // default MUI shrink transform is translate(X, Y) scale(0.75).
                // For RTL, X is negative, so we pick a small negative shift:
                transform: 'translateX(-0.5px) translateY(-6px) scale(0.75)',
              },
            }}
          >
            الفئة الرئيسية
          </InputLabel>
          <Select
            labelId="main-category-select-label"
            value={selectedMainCategory || ''}
            onChange={handleMainCategoryChange}
            label="الفئة الرئيسية"
            inputProps={rtlTextFieldProps.inputProps}
            sx={{...rtlTextFieldProps.sx, '& .MuiSelect-select': { fontWeight: 'bold', textAlign: 'right'}}}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300
                }
              }
            }}
          >
            <MenuItem value="" disabled sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
              <em>اختر الفئة الرئيسية</em>
            </MenuItem>
            {categories.map((mainCat) => (
              <MenuItem
                key={mainCat.id}
                value={mainCat.id}
                sx={{ fontWeight: 'bold', flexDirection: 'row-reverse' }}
              >
                {mainCat.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Keep the error message rendering the same */}
      {selectedMainCategory && getAvailableSubcategories().length === 0 && (
        <Grid item xs={12}>
          <Box sx={{ textAlign: 'right', color: 'error.main' }}>
            لا توجد فئات فرعية لهذه الفئة الرئيسية. الرجاء إضافة فئات فرعية أولاً.
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default CategorySelector;